import { getActingCustomer } from '@/lib/actingContext';
import { supabase } from '@/lib/supabase';

/**
 * Thin wrapper around fetch() that injects the caller's Supabase JWT and
 * decodes a JSON response. All /.netlify/functions/admin-* endpoints
 * expect `Authorization: Bearer ${jwt}` and return `{ ok, ...payload }`.
 *
 * Throws on HTTP non-2xx OR `ok === false` — callers can rely on the
 * resolved value being the successful payload.
 */
export async function adminFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('not signed in');

  // Stamp this TAB's acting-customer context, exactly as the supabase client
  // does (src/lib/supabase.ts). Without it a function resolving the caller's
  // customer falls back to the persisted active_customer_id column, so a
  // superadmin with two tabs open as two different customers would get the
  // OTHER tab's data — on a statement that means another chain's balances.
  // The header is a signal, never the boundary: resolveCallerContext honors it
  // only for a superadmin or an active member of that customer.
  const acting = getActingCustomer();

  const res = await fetch(`/.netlify/functions/${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(acting ? { 'x-acting-customer': acting } : {}),
      ...(init.headers ?? {}),
    },
  });

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // leave as text below
    }
  }

  if (!res.ok) {
    const err =
      parsed && typeof parsed === 'object' && parsed !== null && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : `HTTP ${res.status}: ${text.slice(0, 200)}`;
    throw new Error(err);
  }

  if (parsed && typeof parsed === 'object' && 'ok' in parsed && (parsed as { ok: boolean }).ok === false) {
    const err =
      'error' in parsed && typeof (parsed as { error?: unknown }).error === 'string'
        ? String((parsed as { error: string }).error)
        : 'admin call failed';
    throw new Error(err);
  }

  return parsed as T;
}
